"use client";

import { CartItem, NearbyShowsTicketType } from "./types";
import { ticketTypes } from "./const";
import { CheckoutModalLayout } from "./CheckoutModalLayout";
import { feePercentage, taxPercentage } from "./const";
import { formatPrice } from "./utils";
import { Dispatch, SetStateAction } from "react";
import { Minus, Plus } from "lucide-react";

import { ShowInfo } from "@punchup/data/legacy";
import { cn } from "@punchup/utils/cn";

interface TicketsProps {
  show: ShowInfo;
  cart: CartItem[];
  comedianNames: string[];
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  onAddClick: (ticketType: NearbyShowsTicketType) => void;
  onRemoveClick: (ticketType: NearbyShowsTicketType) => void;
}

export const Tickets = ({
  show,
  cart,
  comedianNames,
  activeStep,
  setActiveStep,
  onAddClick,
  onRemoveClick,
}: TicketsProps) => {
  const ticketCount = cart.reduce((sum, t) => sum + t.quantity, 0);

  const onContinueClick = () => {
    // TODO: validate the cart
    // increment the step index
    setActiveStep((prev) => prev + 1);
  };

  return (
    <CheckoutModalLayout
      show={show}
      comedianNames={comedianNames}
      cart={cart}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      onButtonClick={onContinueClick}
      buttonDisabled={ticketCount === 0}
      title="Tickets"
      subtitle="Choose the type and number of tickets you want to buy"
      buttonLabel="Go to payment details"
    >
      {/* render each ticket type option */}
      <div className="flex flex-col gap-4 md:gap-5">
        {ticketTypes.map((ticketType) => (
          <TicketTypeOption
            key={ticketType.id}
            ticketType={ticketType}
            onAddClick={onAddClick}
            onRemoveClick={onRemoveClick}
            cart={cart}
          />
        ))}
      </div>
    </CheckoutModalLayout>
  );
};

const TicketTypeOption = ({
  ticketType,
  onAddClick,
  onRemoveClick,
  cart,
}: {
  ticketType: NearbyShowsTicketType;
  onAddClick: (ticketType: NearbyShowsTicketType) => void;
  onRemoveClick: (ticketType: NearbyShowsTicketType) => void;
  cart: CartItem[];
}) => {
  const cartItem = cart.find((item) => item.id === ticketType.id);
  const quantity = cartItem?.quantity || 0;

  return (
    <div
      key={ticketType.id}
      className={cn(
        "flex flex-row gap-4 py-3 px-4 md:px-5 md:py-4 border rounded-xl shadow-sm md:shadow-none",
        quantity > 0 ? "border-primary" : "border-[#C7C8C9]"
      )}
    >
      <div className="flex flex-row justify-between w-full">
        <div className="flex flex-col gap-2">
          <p className="text-primary-blue font-semibold text-[14px] md:text-[16px]">
            {ticketType.name}
          </p>
          <div className="flex flex-col md:flex-row gap-1.5 items-start md:items-end">
            <p className="text-primary-blue font-regular text-[16px] leading-5">
              {formatPrice(ticketType.price)}
            </p>
            <p className="text-secondary-blue text-xs">
              incl.{" "}
              {formatPrice(
                ticketType.price * feePercentage +
                  ticketType.price * taxPercentage
              )}{" "}
              fees & taxes
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center">
          <div className="flex flex-row gap-1 md:gap-3 items-center border border-primary-blue rounded-md">
            <button
              onClick={() => onRemoveClick(ticketType)}
              disabled={quantity === 0}
              className="py-3 px-2.5 md:p-2 rounded-l-md transition-colors ease-in-out duration-200 active:bg-gray-100 disabled:text-[#C7C8C9]"
            >
              <Minus className="h-5 w-5" />
            </button>
            <p className="font-semibold text-primary-blue text-[16px] w-4 flex-1 flex flex-row items-center justify-center">
              {quantity}
            </p>
            <button
              onClick={() => onAddClick(ticketType)}
              className="py-3 px-2.5 md:p-2 rounded-r-md transition-colors ease-in-out duration-200 active:bg-gray-100 disabled:text-[#C7C8C9]"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
